import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ArrowLeft, ChevronLeft, ChevronRight, Cog, Download, FileText, RefreshCcw } from "lucide-react";
import { MeterHealthPanel } from "@/components/MeterHealthPanel";
import { YearComparison } from "@/components/YearComparison";
import { SiteSettingsDialog } from "@/components/SiteSettingsDialog";
import { TouBreakdown } from "@/components/TouBreakdown";
import { AiSummary } from "@/components/AiSummary";
import { AiChat } from "@/components/AiChat";
import { Panel } from "@/components/energy/Panel";
import { Donut } from "@/components/energy/Donut";
import { Heatmap } from "@/components/energy/Heatmap";
import { useAuth } from "@/context/AuthContext";
import { monthStart, useSiteEnergy } from "@/lib/useSiteEnergy";
import { num } from "@/lib/format";
import { AXIS, BAR_RADIUS, CHART_MARGIN, LABEL_STYLE, LEGEND_STYLE, TOOLTIP_STYLE } from "@/lib/chartStyles";

const ELEC_COLORS = ["#156F5B", "#4C9F87", "#0E7490", "#7C3AED", "#B45309", "#9CA3AF"];
const GAS_COLORS = ["#C2410C", "#EA580C", "#F59E0B", "#B45309", "#92400E", "#9CA3AF"];
const MODES = [
  { label: "Last 7 days", key: "week" },
  { label: "This month", key: "month" },
  { label: "This year", key: "year" },
];
const BTN_TRANSITION = { transition: "background-color .2s ease, color .2s ease" };
const NAV_BTN = "p-1.5 rounded-md border border-[#E5E7EB] text-[#4D4D4D] hover:border-[#156F5B] hover:text-[#156F5B] disabled:opacity-40";

function ConsumptionTrend({ trend }) {
  if (trend.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-sm text-[#9CA3AF]" data-testid="trend-empty">
        No consumption data
      </div>
    );
  }
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
        <LineChart data={trend} margin={CHART_MARGIN}>
          <CartesianGrid stroke="#F0F2F1" vertical={false} />
          <XAxis dataKey="date" tick={AXIS} interval="preserveStartEnd" minTickGap={40} />
          <YAxis yAxisId="e" tick={AXIS} width={52} />
          <YAxis yAxisId="g" orientation="right" tick={AXIS} width={46} />
          <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={LABEL_STYLE} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          <Line yAxisId="e" type="monotone" dataKey="electricity" name="Electricity (kWh)" stroke="#156F5B" strokeWidth={2} dot={false} />
          <Line yAxisId="g" type="monotone" dataKey="gas" name="Gas (m³)" stroke="#C2410C" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CostBars({ costs, currency }) {
  const format = (v) => `${num(v)} ${currency}`;
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
        <BarChart data={costs} margin={CHART_MARGIN}>
          <CartesianGrid stroke="#F0F2F1" vertical={false} />
          <XAxis dataKey="name" tick={AXIS} />
          <YAxis tick={AXIS} width={56} />
          <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={LABEL_STYLE} formatter={format} />
          <Bar dataKey="value" radius={BAR_RADIUS}>
            {costs.map((c) => (
              <Cell key={c.name} fill={c.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DemandPanel({ demand, currency }) {
  return (
    <Panel
      title="Demand charge"
      subtitle={`${demand.charge_rate} ${currency}/kW · ${demand.basis === "onpeak_peak" ? "on-peak hours" : "any hour"}`}
      testId="panel-demand-charge"
    >
      <div className="flex items-baseline gap-2" data-testid="demand-charge-cost">
        <span className="text-4xl font-semibold font-heading text-[#156F5B]">{num(demand.cost, 2)}</span>
        <span className="text-xs text-[#6B7280] font-heading">{currency}</span>
      </div>
      <p className="mt-3 k-sublabel">
        Billed on {num(demand.billing_peak_kw, 1)} kW, set on{" "}
        {new Date(`${demand.peak_day}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}{" "}
        at {String(demand.peak_hour).padStart(2, "0")}:00
        {demand.window ? ` (${demand.window})` : ""}.
      </p>
      {demand.dis && <p className="mt-1 k-sublabel">{demand.dis}</p>}
    </Panel>
  );
}

function EuiPanel({ data, rangeLabel }) {
  return (
    <Panel title="Electricity Use Intensity" subtitle={data.area ? `${num(data.area)} ft²` : ""} testId="panel-eui">
      <div className="h-64 flex flex-col items-center justify-center">
        <div className="text-6xl font-heading font-semibold text-[#156F5B]" data-testid="eui-value">
          {data.eui != null ? data.eui.toFixed(3) : "—"}
        </div>
        <div className="mt-2 k-sublabel">kWh per ft² · {rangeLabel}</div>
        {data.peak_demand && (
          <div className="mt-6 text-center">
            <div className="k-sublabel">Peak demand</div>
            <div className="text-xl font-heading font-semibold text-[#212121]">{num(data.peak_demand.peak_kw)} kW</div>
          </div>
        )}
      </div>
    </Panel>
  );
}

export default function SiteEnergyPage() {
  const { siteId } = useParams();
  const { user } = useAuth();
  const [month, setMonth] = useState(() => monthStart(new Date()));
  const [mode, setMode] = useState("month");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { data, params, loading, refreshing, building, err, load } = useSiteEnergy(siteId, mode, month);

  const t = data?.totals || {};
  const currency = data?.currency || "CAD";
  const trend = data?.trend || [];
  const costs = useMemo(() => [
    { name: "Total", value: t.total_cost || 0, fill: "#156F5B" },
    { name: "Electricity", value: t.elec_cost || 0, fill: "#4C9F87" },
    { name: "Gas", value: t.gas_cost || 0, fill: "#C2410C" },
    { name: "Demand", value: t.demand_cost || 0, fill: "#0E7490" },
  ], [t.total_cost, t.elec_cost, t.gas_cost, t.demand_cost]);

  const rangeLabel =
    mode === "week"
      ? "Last 7 days"
      : mode === "year"
        ? `${new Date().getFullYear()} year to date`
        : month.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const yoyYear = new Date(`${params.start}T00:00:00`).getFullYear();

  return (
    <div className="max-w-7xl fade-up">
      <Link to={`/sites/${encodeURIComponent(siteId)}`} className="k-sublabel inline-flex items-center gap-1.5 hover:text-[#156F5B]" data-testid="back-to-site">
        <ArrowLeft className="h-3 w-3" /> Back to site
      </Link>

      <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="k-label">Energy summary</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]" data-testid="site-energy-title">
            {loading ? "Loading…" : data?.site_dis || "Site"}
          </h1>
          <p className="mt-2 text-sm text-[#6B7280]">
            {rangeLabel}
            {data?.anchored_to_latest_data && (
              <span className="ml-1 text-[#B45309]" data-testid="energy-anchored-note">
                · showing the latest {params.days} days of available data
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} disabled={mode !== "month"} data-testid="energy-prev-month" className={NAV_BTN}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-heading font-semibold text-[#212121] min-w-[130px] text-center" data-testid="energy-range-label">{rangeLabel}</span>
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} disabled={mode !== "month"} data-testid="energy-next-month" className={NAV_BTN}>
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="flex rounded-md border border-[#E5E7EB] overflow-hidden">
            {MODES.map((p) => (
              <button
                key={p.key}
                onClick={() => { setMode(p.key); if (p.key === "month") setMonth(monthStart(new Date())); }}
                data-testid={`energy-preset-${p.key}`}
                className={`px-3 py-1.5 text-xs font-heading font-semibold ${mode === p.key ? "bg-[#156F5B] text-white" : "bg-white text-[#4D4D4D] hover:bg-[#F3F8F7]"}`}
                style={BTN_TRANSITION}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={() => load(true)} disabled={refreshing} data-testid="energy-refresh-btn" className="k-btn-outline">
            <RefreshCcw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
          <Link to={`/sites/${encodeURIComponent(siteId)}/equipment`} className="k-btn-outline" data-testid="site-equipment-link-energy">
            <Cog className="h-3 w-3" /> Equipment
          </Link>
          {user?.role === "admin" && (
            <button onClick={() => setSettingsOpen(true)} className="k-btn-outline" data-testid="site-settings-btn">
              <Cog className="h-3 w-3" /> Rates &amp; report
            </button>
          )}
          <a
            href={`${API_BASE}/sites/${encodeURIComponent(siteId)}/energy/report?start=${params.start}&days=${params.days}`}
            target="_blank"
            rel="noreferrer"
            className="k-btn-outline"
            data-testid="site-energy-report-btn"
          >
            <FileText className="h-3 w-3" /> Report PDF
          </a>
          <button
            onClick={() =>
              downloadCsv(
                `${data?.site_dis || "site"}_energy_${data?.start}.csv`,
                ["Date", "Electricity (kWh)", "Gas (m3)"],
                trend.map((d) => [d.date, d.electricity, d.gas])
              )
            }
            disabled={loading || trend.length === 0}
            data-testid="site-energy-export-btn"
            className="k-btn-outline"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>
      </div>

      {building && (
        <div className="mt-6 k-card p-4 flex items-center gap-3 text-sm text-[#4D4D4D]" data-testid="site-energy-building">
          <RefreshCcw className="h-4 w-4 animate-spin text-[#156F5B]" />
          Loading this site's meter history into the portal — tiles fill in automatically and stay fast afterwards.
        </div>
      )}
      {!building && data?.history_updated_at && (
        <p className="mt-4 text-xs text-[#9CA3AF]" data-testid="energy-live-status">
          Live · meter data last updated {new Date(data.history_updated_at).toLocaleString()}
        </p>
      )}

      {err ? (
        <div className="mt-8 k-card p-6 text-sm text-red-600 flex items-center justify-between gap-4" data-testid="site-energy-error">
          <span>{err}</span>
          <button onClick={() => load(true)} className="k-btn-outline" data-testid="site-energy-retry-btn">
            <RefreshCcw className="h-3 w-3" /> Try again
          </button>
        </div>
      ) : loading || !data ? (
        <div className="mt-8 k-card p-10 text-center k-sublabel" data-testid="site-energy-loading">
          Building the energy summary…
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Panel
            title="Total Elec and Gas consumption"
            subtitle={`${num(t.electricity_kwh)} kWh · ${num(t.gas_m3)} m³`}
            testId="panel-elec-gas-trend"
            className="lg:col-span-2"
          >
            <ConsumptionTrend trend={trend} />
          </Panel>

          <EuiPanel data={data} rangeLabel={rangeLabel} />

          <Panel title="Total Elec Consumption" subtitle={data.primary_meters?.electricity || `${num(t.electricity_kwh)} kWh`} testId="panel-elec-donut">
            <Donut rows={data.elec_meters || []} colors={ELEC_COLORS} unit="kWh" testId="donut-elec" />
          </Panel>

          <Panel title="Energy Costs" subtitle={`${num(t.total_cost)} ${currency}`} testId="panel-costs">
            <CostBars costs={costs} currency={currency} />
          </Panel>

          <Panel title="Total Gas Consumption by Boiler" subtitle={`${num(t.gas_m3)} m³`} testId="panel-gas-donut">
            <Donut rows={data.gas_boilers || []} colors={GAS_COLORS} unit="m³" testId="donut-gas" />
          </Panel>

          {data.demand && <DemandPanel demand={data.demand} currency={data.currency} />}

          {data.tou?.periods?.length > 0 && (
            <Panel
              title="Cost by rate period"
              subtitle={`Blended ${data.tou.effective_rate} ${data.currency}/kWh`}
              testId="panel-tou"
              className="lg:col-span-2"
            >
              <TouBreakdown tou={data.tou} currency={data.currency} />
            </Panel>
          )}

          <Panel title={`${yoyYear} vs last year`} subtitle="Electricity by month" testId="panel-yoy" className="lg:col-span-3">
            <YearComparison siteId={siteId} year={yoyYear} />
          </Panel>

          <Panel title="Total Energy Use by Time of Day" subtitle="kWh per hour" testId="panel-heatmap" className="lg:col-span-3">
            <Heatmap hourly={data.hourly || []} />
          </Panel>

          {(data.flagged_meters || []).length > 0 && (
            <Panel
              title="Meter health"
              subtitle={`${data.flagged_meters.length} meter${data.flagged_meters.length === 1 ? "" : "s"} need attention`}
              testId="panel-meter-health"
              className="lg:col-span-3"
            >
              <MeterHealthPanel meters={data.flagged_meters} />
            </Panel>
          )}
        </div>
      )}

      {data?.computed_at && (
        <p className="mt-4 text-xs text-[#9CA3AF]" data-testid="energy-rate-note">
          Costs use {data.rates_scope === "site" ? "this building's own rates" : "the portfolio rates"}
          {data.tou ? " with time-of-use bands" : ` (${data.rates.elec_rate}/kWh · ${data.rates.gas_rate}/m³)`}. Last read{" "}
          {new Date(data.computed_at).toLocaleString()}.
        </p>
      )}

      {data?.totals && <AiSummary siteId={siteId} start={params.start} days={params.days} />}
      {data?.totals && user?.role === "admin" && (
        <AiChat siteId={siteId} start={params.start} days={params.days} />
      )}

      <SiteSettingsDialog
        siteId={siteId}
        siteDis={data?.site_dis || "Site"}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={() => load(true)}
      />
    </div>
  );
}
